import { useCallback, useEffect, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { type ApiError, isApiErrorResponse } from 'ts/common/common';
import { type IFetchHookResponse, useTranslate } from 'ts/common/hooks';
import { FetchMethod, useClientApiFetch } from 'client_react/bootstrap';
import { getQuestionnaireEmailStorageKey } from 'client_react/questionnaire/hooks/common';

interface Props {
    email?: Nullable<string>;
    questionnairePublicId?: Nullable<string>;
}

export type QuestionnaireContext = {
    questionnaire?: Nullable<SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire>;
    fetchQuestionnaire: (
        email?: string
    ) => Promise<
        IFetchHookResponse<
            SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire | ApiError
        >
    >;
    loading: boolean;
    saveQuestionResponse: (
        questionId: number,
        responseText: string | null
    ) => Promise<IFetchHookResponse<null | ApiError>>;
    saving: boolean;
    submitQuestionnaire: (options?: {
        showSuccessToast?: boolean;
    }) => Promise<IFetchHookResponse<null | ApiError>>;
    submitting: boolean;
    requestFailed: boolean;
    error: Nullable<ApiError>;
};

const useQuestionnaire = ({
    email: existingEmail,
    questionnairePublicId
}: Props): QuestionnaireContext => {
    const t = useTranslate('clientQuestionnaire');
    const toast = useToast();

    const [error, setError] = useState<Nullable<ApiError>>(null);
    const [requestFailed, setRequestFailed] = useState(false);
    const [questionnaire, setQuestionnaire] = useState<
        SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire
    >();

    const { loading, performFetch } = useClientApiFetch<SpApi.Client.IQuestionnaire | ApiError>(
        `questionnaire/${questionnairePublicId}`,
        {
            defer: true,
            method: FetchMethod.GET
        }
    );

    const fetchQuestionnaire = useCallback(
        async (email?: Nullable<string>) => {
            if (!questionnairePublicId) {
                return null;
            }

            const response = await performFetch({
                search: email ? { email } : undefined
            });

            if (response && !isApiErrorResponse(response)) {
                setQuestionnaire(response);
                setError(null);
                setRequestFailed(false);

                if (email) {
                    const questionnaireEmailStorageKey =
                        getQuestionnaireEmailStorageKey(questionnairePublicId);
                    sessionStorage.setItem(questionnaireEmailStorageKey, email);
                }
            } else {
                setError(response);
                setRequestFailed(true);
            }

            return response;
        },
        [performFetch, questionnairePublicId]
    );

    useEffect(() => {
        if (!questionnairePublicId) {
            return;
        }

        const questionnaireEmailStorageKey = getQuestionnaireEmailStorageKey(questionnairePublicId);
        const email = existingEmail ?? sessionStorage.getItem(questionnaireEmailStorageKey);

        (async () => {
            const response = await fetchQuestionnaire(email);

            if (response?.status === 403) {
                sessionStorage.removeItem(questionnaireEmailStorageKey);
                await fetchQuestionnaire();
            }
        })();
    }, [existingEmail, fetchQuestionnaire, questionnairePublicId]);

    const { loading: saving, performFetch: patchQuestionnaire } = useClientApiFetch<unknown>(
        `questionnaire/${questionnairePublicId}`,
        {
            defer: true,
            method: FetchMethod.PATCH
        }
    );

    const saveQuestionResponse = useCallback(
        async (questionId: number, responseText: string | null) => {
            if (!questionnairePublicId) {
                throw new Error('Questionnaire public ID is required to save a question response');
            }

            const questionnaireEmailStorageKey =
                getQuestionnaireEmailStorageKey(questionnairePublicId);
            const email = existingEmail ?? sessionStorage.getItem(questionnaireEmailStorageKey);

            const response = await patchQuestionnaire({
                data: { id: questionId, response_text: responseText },
                search: email ? { email } : undefined
            });

            if (response && !isApiErrorResponse(response)) {
                setError(null);
                setRequestFailed(false);

                setQuestionnaire((prev) => {
                    if (!prev || !('pages' in prev)) {
                        return prev;
                    }

                    return {
                        ...prev,
                        questionnaireStatus: 'partial',
                        pages: prev.pages.map((page) => ({
                            ...page,
                            questions: page.questions.map((q) =>
                                q.id === questionId ? { ...q, responseText } : q
                            )
                        }))
                    };
                });
            } else {
                setError(response);
                setRequestFailed(true);
                toast({
                    title: t('error'),
                    description: response?.detail ?? t('errorSavingQuestionnaire'),
                    status: 'error'
                });
            }

            return response;
        },
        [existingEmail, patchQuestionnaire, questionnairePublicId, setQuestionnaire, toast, t]
    );

    const { loading: submitting, performFetch: postSubmission } = useClientApiFetch<unknown>(
        `questionnaire/${questionnairePublicId}/submission`,
        {
            defer: true,
            method: FetchMethod.POST
        }
    );

    const submitQuestionnaire = useCallback(
        async ({ showSuccessToast = true }: { showSuccessToast?: boolean } = {}) => {
            if (!questionnairePublicId) {
                throw new Error('Questionnaire public ID is required to submit a questionnaire');
            }

            const questionnaireEmailStorageKey =
                getQuestionnaireEmailStorageKey(questionnairePublicId);
            const email = existingEmail ?? sessionStorage.getItem(questionnaireEmailStorageKey);

            if (!email) {
                toast({
                    title: t('error'),
                    description: t('emailHeading'),
                    status: 'error'
                });
                return null;
            }

            const response = await postSubmission({
                search: { email }
            });

            if (response && !isApiErrorResponse(response)) {
                setError(null);
                setRequestFailed(false);

                if (showSuccessToast) {
                    toast({
                        title: t('submitSuccess'),
                        description: t('submitSuccessMessage'),
                        status: 'success'
                    });
                }

                setQuestionnaire((prev) => {
                    if (!prev || !('pages' in prev)) {
                        return prev;
                    }

                    return {
                        ...prev,
                        questionnaireStatus: 'completed',
                        completed: new Date().toISOString()
                    };
                });
            } else {
                const description =
                    response && 'info' in response && Array.isArray(response.info)
                        ? response.info.join(' ')
                        : response?.detail ?? t('errorSavingQuestionnaire');
                setError(response);
                setRequestFailed(true);
                toast({
                    title: t('error'),
                    description,
                    status: 'error'
                });
            }

            return response;
        },
        [existingEmail, postSubmission, questionnairePublicId, setQuestionnaire, toast, t]
    );

    return {
        questionnaire,
        fetchQuestionnaire,
        loading,
        saveQuestionResponse,
        saving,
        submitQuestionnaire,
        submitting,
        requestFailed,
        error
    };
};

export default useQuestionnaire;
